import { API_BASE_URL } from '../config';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('SchedulerClient');

// Types for scheduler API
export interface RunTarget {
  runTargetName: string;
  [key: string]: unknown;
}

export interface RunTargetsResponse {
  content: Record<string, RunTarget>;
}

export interface ExecutionRequest {
  /** Project zip file. If omitted, backend builds from project-home (Option B). */
  project?: File;
  /** Project name for Option B when project is not provided (e.g. "project-test"). */
  projectName?: string;
  type: string;
  file: string;
  runTarget: string;
  logLevel: string;
  runConfig: string;
}

export interface ExecutionResponse {
  id: string;
  executionId?: string;
}

export interface ExecutionStatus {
  id: string;
  status: string;
  creationDateTime?: string;
  finishedDateTime?: string;
  startTime?: string;
  endTime?: string;
  error?: string;
  message?: string;
  result?: unknown;
  logLevel?: string;
  runTarget?: string;
  pipelineName?: string;
  [key: string]: unknown;
}

export interface LogsResponse {
  log?: string | unknown[];
  [key: string]: unknown;
}

export interface ExecutionListResponse {
  content: ExecutionInfo[];
  page?: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
  [key: string]: unknown;
}

export interface ExecutionInfo {
  executionId: number;
  status?: string;
  bundleName?: string;
  runTargetName?: string;
  creationDateTime?: string;
  [key: string]: unknown;
}

export interface ExecutionDetail {
  executionId: number;
  status: string;
  bundleName?: string;
  runTargetName?: string;
  creationDateTime: string;
  initializationDateTime?: string;
  runStartDateTime?: string;
  finishedDateTime?: string;
  [key: string]: unknown;
}

export interface ExecutionLogs {
  executionId: number;
  type: string;
  name: string;
  filename?: string;
  status: string;
  startDate?: string;
  endDate?: string;
  log?: string;
  [key: string]: unknown;
}

export interface ExecutionMetrics {
  content: ExecutionMetric[];
  page?: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

export interface ExecutionMetric {
  executionId: string;
  logChannelId?: string;
  timestamp: number;
  linesRead: number;
  linesOutput: number;
  linesInput: number;
  linesError: number;
  linesRejected: number;
  linesBufferIn: number;
  linesWritten: number;
  linesBufferOut: number;
  linesUpdated: number;
  [key: string]: unknown;
}

class SchedulerClient {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL.replace(/\/$/, ''); // Remove trailing slash
    logger.debug('SchedulerClient initialized:', { baseURL: this.baseURL });
  }

  /**
   * Get available run targets
   */
  async getRunTargets(): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseURL}/v1/runTargets`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch run targets: ${response.status} ${response.statusText}`);
      }

      const data: RunTargetsResponse = await response.json();
      
      // Extract run target names using the specified JSON path: $.content.*.runTargetName
      const content = data?.content || {};
      const runTargetNames = Object.values(content)
        .map((item: RunTarget) => item.runTargetName)
        .filter((name): name is string => typeof name === 'string');

      logger.debug('Run targets fetched:', { count: runTargetNames.length, names: runTargetNames });
      return runTargetNames;
    } catch (error) {
      logger.error('Error fetching run targets:', error);
      throw error;
    }
  }

  /**
   * Run a pipeline
   */
  async runPipeline(request: ExecutionRequest): Promise<string> {
    try {
      const formData = new FormData();
      if (request.project) {
        formData.append('project', request.project);
      }
      if (request.projectName) {
        formData.append('projectName', request.projectName);
      }
      formData.append('type', request.type);
      formData.append('file', request.file);
      formData.append('runTarget', request.runTarget);
      formData.append('logLevel', request.logLevel);
      formData.append('runConfig', request.runConfig);

      const response = await fetch(`${this.baseURL}/v1/executions/run`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to run pipeline: ${response.status} ${response.statusText}`);
      }

      // Try to parse as JSON first, fallback to text
      let executionId: string;
      const responseText = await response.text();
      
      try {
        const responseJson: ExecutionResponse = JSON.parse(responseText);
        executionId = responseJson.id || responseJson.executionId || '';
      } catch {
        // If JSON parsing fails, use the response text as execution ID
        executionId = responseText.trim();
      }

      if (!executionId) {
        throw new Error('No execution ID returned from run API');
      }

      logger.debug('Pipeline run started:', { executionId, file: request.file });
      return executionId;
    } catch (error) {
      logger.error('Error running pipeline:', error);
      throw error;
    }
  }

  /**
   * Get execution status
   */
  async getExecutionStatus(executionId: string): Promise<ExecutionStatus> {
    try {
      const response = await fetch(`${this.baseURL}/v1/executions/${executionId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch execution status: ${response.status} ${response.statusText}`);
      }

      const data: ExecutionStatus = await response.json();
      logger.debug('Execution status fetched:', { executionId, status: data.status });
      return data;
    } catch (error) {
      logger.error('Error fetching execution status:', error);
      throw error;
    }
  }

  /**
   * Get execution logs
   */
  async getExecutionLogs(executionId: string): Promise<string> {
    try {
      const response = await fetch(`${this.baseURL}/v1/logs/${executionId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch logs: ${response.status} ${response.statusText}`);
      }

      const data = await response.text();
      logger.debug('Execution logs fetched:', { executionId, dataLength: data.length });
      return data;
    } catch (error) {
      logger.error('Error fetching execution logs:', error);
      throw error;
    }
  }

  /**
   * Get recent executions (for dashboard)
   */
  async getRecentExecutions(limit: number = 10): Promise<ExecutionInfo[]> {
    const response = await fetch(`${this.baseURL}/v1/executions`);
    if (!response.ok) {
      throw new Error(`Failed to fetch executions: ${response.status} ${response.statusText}`);
    }
    const data: ExecutionListResponse = await response.json();
    const content = data?.content || [];
    return content.slice(0, limit);
  }

  /**
   * Get execution by ID (alias for getExecutionDetail for compatibility)
   */
  async getExecutionById(executionId: number): Promise<ExecutionDetail> {
    return this.getExecutionDetail(executionId);
  }

  /**
   * Get executions count
   * Fetches executions from the API and returns the total count from pagination info
   */
  async getExecutionsCount(): Promise<number> {
    try {
      const response = await fetch(`${this.baseURL}/v1/executions`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch executions: ${response.status} ${response.statusText}`);
      }

      const data: ExecutionListResponse = await response.json();
      
      // Use totalElements from pagination info if available, otherwise count the content array
      const totalCount = data?.page?.totalElements;
      if (typeof totalCount === 'number' && totalCount >= 0) {
        logger.debug('Total executions count fetched from pagination:', { totalCount });
        return totalCount;
      }
      
      // Fallback: Extract execution IDs using JSON path: $.content[*].executionId
      const content = data?.content || [];
      const executionIds = content
        .map((item: ExecutionInfo) => item.executionId)
        .filter((id): id is number => typeof id === 'number' && id > 0);

      const count = executionIds.length;
      logger.debug('Executions count fetched from content array:', { count, executionIds: executionIds.slice(0, 5) }); // Log first 5 IDs for debugging
      return count;
    } catch (error) {
      logger.error('Error fetching executions count:', error);
      throw error;
    }
  }

  /**
   * Get failed executions count
   * Fetches executions from the API and counts those with failed/error statuses
   */
  async getFailedExecutionsCount(): Promise<number> {
    try {
      const response = await fetch(`${this.baseURL}/v1/executions`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch executions: ${response.status} ${response.statusText}`);
      }

      const data: ExecutionListResponse = await response.json();
      
      // Get all executions from the content array
      const content = data?.content || [];
      
      // Count executions with failed/error statuses
      const failedExecutions = content.filter((item: ExecutionInfo) => {
        const status = item.status?.toUpperCase() || '';
        return this.isFailedStatus(status);
      });

      const count = failedExecutions.length;
      logger.debug('Failed executions count fetched:', { 
        count, 
        totalExecutions: content.length,
        statuses: failedExecutions.map(e => e.status).slice(0, 5) 
      });
      return count;
    } catch (error) {
      logger.error('Error fetching failed executions count:', error);
      throw error;
    }
  }

  /**
   * Get running executions count
   * Fetches executions from the API and counts those with active/running statuses
   */
  async getRunningExecutionsCount(): Promise<number> {
    try {
      const response = await fetch(`${this.baseURL}/v1/executions`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch executions: ${response.status} ${response.statusText}`);
      }

      const data: ExecutionListResponse = await response.json();
      
      // Get all executions from the content array
      const content = data?.content || [];
      
      // Count executions with running/active statuses
      const runningExecutions = content.filter((item: ExecutionInfo) => {
        const status = item.status?.toUpperCase() || '';
        return this.isRunningStatus(status);
      });

      const count = runningExecutions.length;
      logger.debug('Running executions count fetched:', { 
        count, 
        totalExecutions: content.length,
        statuses: runningExecutions.map(e => e.status).slice(0, 5) 
      });
      return count;
    } catch (error) {
      logger.error('Error fetching running executions count:', error);
      throw error;
    }
  }

  /**
   * Get completed executions count
   * Fetches executions from the API and counts those with successful completion status
   */
  async getCompletedExecutionsCount(): Promise<number> {
    try {
      const response = await fetch(`${this.baseURL}/v1/executions`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch executions: ${response.status} ${response.statusText}`);
      }

      const data: ExecutionListResponse = await response.json();
      
      // Get all executions from the content array
      const content = data?.content || [];
      
      // Count executions with successful completion statuses
      const completedExecutions = content.filter((item: ExecutionInfo) => {
        const status = item.status?.toUpperCase() || '';
        return this.isCompletedStatus(status);
      });

      const count = completedExecutions.length;
      logger.debug('Completed executions count fetched:', { 
        count, 
        totalExecutions: content.length,
        statuses: completedExecutions.map(e => e.status).slice(0, 5) 
      });
      return count;
    } catch (error) {
      logger.error('Error fetching completed executions count:', error);
      throw error;
    }
  }

  /**
   * Check if a status indicates a successfully completed execution
   */
  private isCompletedStatus(status: string): boolean {
    const completedStatuses = [
      'FINISHED',
      'COMPLETED',
      'SUCCESS',
      'SUCCESSFUL'
    ];
    
    // Check for exact matches or if the status contains any completion keywords
    // Note: FINISHED_WITH_UNKNOWN is considered failed, not completed
    return completedStatuses.some(completedStatus => 
      status === completedStatus || 
      (status.includes(completedStatus) && !status.includes('UNKNOWN') && !status.includes('ERROR'))
    );
  }

  /**
   * Check if a status indicates a running/active execution
   */
  private isRunningStatus(status: string): boolean {
    const runningStatuses = [
      'STARTED',
      'CREATED', 
      'RUNNING',
      'INITIALIZING',
      'PENDING',
      'QUEUED',
      'EXECUTING',
      'PROCESSING'
    ];
    
    // Check for exact matches or if the status contains any running keywords
    return runningStatuses.some(runningStatus => 
      status === runningStatus || 
      status.includes(runningStatus) ||
      status.includes('RUNNING') ||
      status.includes('STARTED') ||
      status.includes('CREATED')
    );
  }

  /**
   * Check if a status indicates a failed execution
   */
  private isFailedStatus(status: string): boolean {
    const failedStatuses = [
      'FAILED',
      'STOPPED',
      'CANCELLED',
      'ERROR',
      'ABORTED',
      'TERMINATED',
      'TIMEOUT',
      'REJECTED'
    ];
    
    // Check for exact matches or if the status contains any failed keywords
    return failedStatuses.some(failedStatus => 
      status === failedStatus || 
      status.includes(failedStatus) ||
      status.includes('FAILED') ||
      status.includes('STOPPED') ||
      status.includes('ERROR')
    ) || 
    // Special cases - statuses that sound like they might be failures
    status.includes('UNKNOWN') ||
    status.includes('INTERRUPTED');
  }

  /**
   * Get detailed execution information
   */
  async getExecutionDetail(executionId: number): Promise<ExecutionDetail> {
    try {
      const response = await fetch(`${this.baseURL}/v1/executions/${executionId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch execution detail: ${response.status} ${response.statusText}`);
      }

      const data: ExecutionDetail = await response.json();
      logger.debug('Execution detail fetched:', { executionId, status: data.status });
      return data;
    } catch (error) {
      logger.error('Error fetching execution detail:', error);
      throw error;
    }
  }

  /**
   * Get execution logs
   */
  async getExecutionLogsDetail(executionId: number): Promise<ExecutionLogs> {
    try {
      const response = await fetch(`${this.baseURL}/v1/logs/${executionId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch execution logs: ${response.status} ${response.statusText}`);
      }

      const data: ExecutionLogs = await response.json();
      logger.debug('Execution logs detail fetched:', { executionId, name: data.name });
      return data;
    } catch (error) {
      logger.error('Error fetching execution logs detail:', error);
      throw error;
    }
  }

  /**
   * Get execution metrics
   */
  async getExecutionMetrics(executionId: number): Promise<ExecutionMetrics> {
    try {
      const response = await fetch(`${this.baseURL}/v1/logs/metrics/${executionId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch execution metrics: ${response.status} ${response.statusText}`);
      }

      const data: ExecutionMetrics = await response.json();
      logger.debug('Execution metrics fetched:', { executionId, metricsCount: data.content.length });
      return data;
    } catch (error) {
      logger.error('Error fetching execution metrics:', error);
      throw error;
    }
  }

  /**
   * Check if execution is in terminal state
   */
  isTerminalState(status: string): boolean {
    const terminalStates = ['COMPLETED', 'FAILED', 'CANCELLED', 'FINISHED'];
    return terminalStates.some(s => s.toLowerCase() === status.toLowerCase());
  }
}

// Export singleton instance
export const schedulerClient = new SchedulerClient();
export default schedulerClient;